import { useMemo, useRef, useEffect } from "react";
import { useGLTF } from "@react-three/drei";
import { Mesh, MathUtils, BufferAttribute, MeshStandardMaterial, Material } from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { degresToRad } from "../../../helpers/angles";

interface RibbingViewProps {
  lightUri: string;
  heavyUri: string;
  ribbingLevel?: number; // 0–10
  isLeft: boolean;
}

export const RibbingView = ({
  lightUri,
  heavyUri,
  ribbingLevel = 0,
  isLeft,
}: RibbingViewProps) => {
  const lightScene = useGLTF(lightUri).scene;
  const heavyScene = useGLTF(heavyUri).scene;

  const meshRef = useRef<Mesh>(null!);

  /* ------------------------------------------------------------ */
  /* Build morphable geometry + material ONCE                     */
  /* ------------------------------------------------------------ */
  const built = useMemo(() => {
    let lightMesh: Mesh | null = null;
    let heavyMesh: Mesh | null = null;

    lightScene.traverse((o) => o instanceof Mesh && (lightMesh = o));
    heavyScene.traverse((o) => o instanceof Mesh && (heavyMesh = o));

    if (!lightMesh || !heavyMesh) {
      console.error("❌ Ribbing mesh not found");
      return null;
    }

    const baseGeom = lightMesh.geometry.clone();
    const heavyPos = heavyMesh.geometry.attributes.position;

    if (baseGeom.attributes.position.count !== heavyPos.count) {
      console.error("❌ Topology mismatch — cannot morph");
      return null;
    }

    baseGeom.morphAttributes = {
      position: [new BufferAttribute(heavyPos.array.slice(0), 3)],
    };
    baseGeom.morphTargetsRelative = false;
    baseGeom.computeVertexNormals();

    // ✅ FIX: clone material & disable vertex colors
    const material = Array.isArray(lightMesh.material)
      ? lightMesh.material.map((m) => {
        const mat = m.clone() as MeshStandardMaterial;
        mat.vertexColors = false;
        mat.needsUpdate = true;
        return mat;
      })
      : (() => {
        const mat = lightMesh.material.clone() as MeshStandardMaterial;
        mat.vertexColors = false;
        mat.needsUpdate = true;
        return mat;
      })();

    return {
      geometry: baseGeom,
      material,
    };
  }, [lightScene, heavyScene, isLeft]);


  /* ------------------------------------------------------------ */
  /* Dispose unique GPU resources on unmount                     */
  /* ------------------------------------------------------------ */
  useEffect(() => {
    return () => {
      if (!built) return;
      built.geometry.dispose();
      const mats: Material[] = Array.isArray(built.material) ? built.material : [built.material];
      mats.forEach((m) => m.dispose());
    };
  }, [built]);

  /* ------------------------------------------------------------ */
  /* Animate morph influence                                      */
  /* ------------------------------------------------------------ */
  const targetT = MathUtils.clamp(ribbingLevel / 10, 0, 1);
  const tRef = useRef(targetT);
  const { invalidate } = useThree();

  useFrame((_, delta) => {
    if (!meshRef.current || !built) return;

    // Clamp delta to one frame max — prevents an instant jump on the first frame
    // after the scene has been idle (demand rendering can accumulate large deltas)
    const clampedDelta = Math.min(delta, 0.064);

    tRef.current = MathUtils.damp(tRef.current, targetT, 3, clampedDelta);
    meshRef.current.morphTargetInfluences![0] = tRef.current;

    // Keep requesting frames until animation settles
    if (Math.abs(tRef.current - targetT) > 0.001) {
      invalidate();
    }
  });

  if (!built) return null;

  // Position and rotation is same as the Insole's
  const position = isLeft ? [-0.0062, -0.002, 0.0008] : [-0.0062, -0.002, 0];
  const rotation: [number, number, number] =  isLeft ? [degresToRad(-5), degresToRad(95), 0] : [degresToRad(-5), degresToRad(85), 0];

  return (
    <group position={position} rotation={rotation}>
      <mesh
        ref={meshRef}
        geometry={built.geometry}
        material={built.material}
        morphTargetInfluences={[targetT]}
      />
    </group>
  );
};
